/* eslint-disable no-nested-ternary */
import {
  EXTENSION_URL,
  IMAGING_AND_TESTS_AVAILABLE_IMAGES,
  IMAGING_AND_TESTS_TEST_NAME,
  IMAGING_AND_TESTS_REPORTED_DATE,
  IMAGING_AND_TESTS_HISTORY,
  IMAGING_AND_TESTS_TECHNIQUE,
  IMAGING_AND_TESTS_FINDINGS,
  IMAGING_AND_TESTS_RECOMMENDATIONS,
  LAB_REPORT_TEST_NAME,
} from '@constants/extension-codes';
import { IMAGING_TEST_DIAGNOSTIC_REPORT_TYPE_MAP } from '@constants/option-types/imaging-test';
import { LAB_REPORT_NAME_MAP } from '@constants/option-types/lab';

import {
  DiagnosticReportRecord,
  type DiagnosticReportValues,
} from '@models/diagnostic-report';

import { formatPatientId } from '@utils/format';

import type { DiagnosticReportInput, Extension } from 'generated/types';

const DIAGNOSTIC_REPORT_TYPE_MAP: Record<string, string> = {
  'imaging-and-tests': 'Imaging and Tests',
  laboratory: 'Lab Results',
};

type DiagnosticReportType = 'imaging-and-tests' | 'laboratory';

export function getDiagnosticReportTemplate(
  patient_id: string,
  values: DiagnosticReportValues,
  type: DiagnosticReportType,
  /**
   * (Lab reports) - lab observation ids formatted as `Observation/${id}`
   * (Imaging and Tests) - imaging study ids
   */
  observations?: string[]
): DiagnosticReportInput {
  const extensions: Extension[] = [];
  // values.testName
  if (values.title) {
    if (type === 'imaging-and-tests') {
      extensions.push({
        url: `${EXTENSION_URL}${IMAGING_AND_TESTS_TEST_NAME}`,
        valueString: values.title,
      });
    } else {
      extensions.push({
        url: `${EXTENSION_URL}${LAB_REPORT_TEST_NAME}`,
        valueString: values.title,
      });
    }
  }
  if (values.reportedDate) {
    extensions.push({
      url: `${EXTENSION_URL}${IMAGING_AND_TESTS_REPORTED_DATE}`,
      valueDateTime: values.reportedDate,
    });
  }
  if (values.history) {
    extensions.push({
      url: `${EXTENSION_URL}${IMAGING_AND_TESTS_HISTORY}`,
      valueString: values.history,
    });
  }
  if (values.technique) {
    extensions.push({
      url: `${EXTENSION_URL}${IMAGING_AND_TESTS_TECHNIQUE}`,
      valueString: values.technique,
    });
  }
  if (values.findings) {
    extensions.push({
      url: `${EXTENSION_URL}${IMAGING_AND_TESTS_FINDINGS}`,
      valueString: values.findings,
    });
  }
  if (values.recommendations) {
    extensions.push({
      url: `${EXTENSION_URL}${IMAGING_AND_TESTS_RECOMMENDATIONS}`,
      valueString: values.recommendations,
    });
  }
  if (type === 'imaging-and-tests') {
    extensions.push({
      url: `${EXTENSION_URL}${IMAGING_AND_TESTS_AVAILABLE_IMAGES}`,
      valueBoolean: values.imagesAvailable,
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensions.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  return {
    resourceType: 'DiagnosticReport',
    status: 'final',
    subject: {
      reference: formatPatientId(patient_id),
    },
    category: [
      {
        coding: [
          {
            code: type,
            display: DIAGNOSTIC_REPORT_TYPE_MAP[type],
            system: DiagnosticReportRecord.VITALL_SYSTEM,
          },
        ],
        text: DIAGNOSTIC_REPORT_TYPE_MAP[type],
      },
    ],
    code: {
      coding: [
        {
          code:
            type === 'imaging-and-tests'
              ? values.typeName
                ? IMAGING_TEST_DIAGNOSTIC_REPORT_TYPE_MAP[values.typeName]
                : 'other'
              : values.code,
          display:
            type === 'imaging-and-tests'
              ? values.typeName
              : values.code
                ? LAB_REPORT_NAME_MAP[values.code]
                : 'Other',
          system: DiagnosticReportRecord.VITALL_SYSTEM,
        },
      ],
      text:
        type === 'imaging-and-tests'
          ? values.typeName
          : values.code
            ? LAB_REPORT_NAME_MAP[values.code]
            : 'Other',
    },
    ...(values.details && {
      conclusion: values.details,
    }),
    ...(values.testDate && {
      effectiveDateTime: values.testDate,
    }),
    ...(extensions.length > 0 && {
      extension: extensions,
    }),
    ...(values.practitioner && {
      performer: [
        {
          display: values.practitioner,
        },
      ],
    }),
    // Associate lab observations with lab report
    ...(type === 'laboratory' &&
      observations?.length && {
        result: observations.map((obs) => ({ reference: obs })),
      }),
  };
}
